import React, { useEffect, useCallback } from "react";
import { MapPin, House, Search, Loader2 } from "lucide-react";
import { useUser } from "../../context/UserContext";
import { useApi } from "../../utils/Api";

const STRIPE_RETURN_FLAG = "stripe_delivery_return_pending";

export default function DeliveryForm({
  postcode,
  setPostcode,
  house,
  setHouse,
  deliveryAddress,
  setDeliveryAddress,
  deliveryFee,
  setDeliveryFee,
  notes,
  setNotes,
  setErrorMessage,
  lookingUp,
  setLookingUp,
  setIsLoading,
}) {
  const { user, userLoading, openLoginModal } = useUser();
  const { apiFetch } = useApi();

  const isGuest = !user;

  const [showRestrictionModal, setShowRestrictionModal] = React.useState(false);
  const [hasAgreedRestriction, setHasAgreedRestriction] = React.useState(false);

  const clearReturnedStripeDeliveryState = useCallback(() => {
    const shouldInvalidate = sessionStorage.getItem(STRIPE_RETURN_FLAG) === "1";

    if (!shouldInvalidate) return;

    sessionStorage.removeItem(STRIPE_RETURN_FLAG);

    setHouse("");
    setDeliveryAddress("");
    setDeliveryFee(null);
    setErrorMessage("");
  }, [setHouse, setDeliveryAddress, setDeliveryFee, setErrorMessage]);

  const getFriendlyDeliveryError = useCallback(
    (message = "", errorCode = "") => {
      if (errorCode === "OUTSIDE_DELIVERY_RADIUS") {
        return "Sorry, this address is outside our delivery radius of 4.5 miles.";
      }

      const text = String(message).toLowerCase();

      if (
        text.includes("do not deliver") ||
        text.includes("deliver more than") ||
        text.includes("outside delivery radius") ||
        text.includes("out of range")
      ) {
        return "Sorry, this address is outside our delivery radius of 4.5 miles.";
      }

      if (text.includes("unavailable")) {
        return "Delivery service is unavailable right now. Please try again shortly.";
      }

      if (text.includes("timeout") || text.includes("timed out")) {
        return "We could not reach the delivery service right now. Please try again shortly.";
      }

      if (
        text.includes("not found") ||
        text.includes("could not find") ||
        text.includes("address")
      ) {
        return "We could not find that address. Please check your postcode and house number.";
      }

      return "We could not confirm your delivery address right now. Please try again.";
    },
    [],
  );

  useEffect(() => {
    setIsLoading(false);

    if (userLoading || !user) return;

    const fetchLastDeliveryAddress = async () => {
      setIsLoading(true);

      try {
        const res = await apiFetch("last-delivery-address");

        if (res?.success) {
          setPostcode(res.postcode || "");
          setHouse("");
          setDeliveryAddress("");
          setDeliveryFee(null);
        }
      } catch (error) {
        console.error("last-delivery-address error:", error);
      } finally {
        setIsLoading(false);
        clearReturnedStripeDeliveryState();
      }
    };

    fetchLastDeliveryAddress();

    return () => setIsLoading(false);
  }, [
    user,
    userLoading,
    apiFetch,
    setIsLoading,
    setPostcode,
    setHouse,
    setDeliveryAddress,
    setDeliveryFee,
    clearReturnedStripeDeliveryState,
  ]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      clearReturnedStripeDeliveryState();
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [clearReturnedStripeDeliveryState]);

  const requireLogin = (e) => {
    if (!isGuest || userLoading) return false;

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    openLoginModal();
    return true;
  };

  const lookupAddressAndFee = async (e) => {
    if (requireLogin(e)) return;

    const pc = (postcode || "").trim();
    const h = (house || "").trim();

    if (!pc || !h) {
      setErrorMessage("Please enter postcode and house/flat.");
      return;
    }

    setLookingUp(true);
    setErrorMessage("");
    setDeliveryAddress("");
    setDeliveryFee(null);

    try {
      const res = await apiFetch("lookup-delivery-details", {
        postcode: pc,
        house: h,
        customer_id: user?.id || 0,
      });

      if (!res?.success) {
        setErrorMessage(
          getFriendlyDeliveryError(res?.message, res?.error_code),
        );
        return;
      }

      sessionStorage.removeItem(STRIPE_RETURN_FLAG);
      setDeliveryAddress(res.address || "");
      setDeliveryFee(Number(res.fee));
    } catch (error) {
      console.error("lookupAddressAndFee error:", error);
      setErrorMessage(
        "We could not confirm your delivery address right now. Please try again.",
      );
      setDeliveryAddress("");
      setDeliveryFee(null);
    } finally {
      setLookingUp(false);
    }
  };

  return (
    <section>
      <div className="space-y-6 p-2">
        {isGuest && (
          <div className="rounded-2xl border border-[#fa9324]/40 bg-[#fa9324]/10 p-4 text-sm font-medium text-black">
            Please log in to continue with delivery.
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold text-black">Delivery Details</h2>
          <p className="text-sm text-black/60">
            Enter your address details to find your delivery fee.
          </p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-black">
            Postcode
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/50">
              <MapPin size={18} />
            </span>
            <input
              value={postcode}
              onChange={(e) => {
                if (isGuest) return;
                setPostcode(e.target.value);
                setErrorMessage("");
                setDeliveryFee(null);
                setDeliveryAddress("");
              }}
              onFocus={requireLogin}
              onClick={requireLogin}
              readOnly={isGuest || lookingUp}
              placeholder="Enter postcode"
              className="w-full rounded-2xl border border-black/20 bg-white py-3.5 pl-11 pr-4 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20 disabled:cursor-not-allowed disabled:bg-black/5"
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-black">
            House Number / Flat
          </label>

          <div className="flex items-stretch gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-black/50">
                <House size={18} />
              </span>
              <input
                value={house}
                onChange={(e) => {
                  if (isGuest || !hasAgreedRestriction) return;
                  setHouse(e.target.value);
                  setErrorMessage("");
                  setDeliveryFee(null);
                  setDeliveryAddress("");
                }}
                onFocus={(e) => {
                  if (requireLogin(e)) return;

                  if (!hasAgreedRestriction) {
                    e.preventDefault();
                    setShowRestrictionModal(true);
                    return;
                  }
                }}
                onClick={(e) => {
                  if (requireLogin(e)) return;

                  if (!hasAgreedRestriction) {
                    e.preventDefault();
                    setShowRestrictionModal(true);
                    return;
                  }
                }}
                readOnly={isGuest || lookingUp || !hasAgreedRestriction}
                placeholder="e.g. 10, Flat 2"
                className="w-full rounded-2xl border border-black/20 bg-white py-3.5 pl-11 pr-4 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20 disabled:cursor-not-allowed disabled:bg-black/5"
              />
            </div>

            <button
              type="button"
              onClick={lookupAddressAndFee}
              disabled={
                isGuest ||
                lookingUp ||
                !postcode.trim() ||
                !house.trim() ||
                !hasAgreedRestriction
              }
              className={`shrink-0 inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 font-semibold transition ${
                isGuest || lookingUp || !postcode.trim() || !house.trim()
                  ? "cursor-not-allowed bg-[#fa9324]/50 text-black"
                  : "bg-[#fa9324] text-black shadow-[0_10px_25px_rgba(0,0,0,0.18)] hover:-translate-y-[1px] hover:opacity-90"
              }`}
            >
              {lookingUp ? "Finding..." : "Find"}
              {lookingUp ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
            </button>
          </div>
        </div>

        {lookingUp && (
          <div className="rounded-2xl border border-[#fa9324]/30 bg-[#fa9324]/10 px-4 py-4">
            <div className="flex items-center gap-3">
              <Loader2 size={20} className="animate-spin text-black" />
              <div>
                <p className="text-sm font-semibold text-black">
                  Looking up your delivery details...
                </p>
                <p className="text-sm text-black/65">
                  Please wait while we confirm your address and delivery fee.
                </p>
              </div>
            </div>
          </div>
        )}

        {!lookingUp && deliveryAddress && (
          <div>
            <label className="mb-2 block text-sm font-medium text-black">
              Delivery Address
            </label>
            <textarea
              value={deliveryAddress}
              readOnly
              rows={2}
              className="w-full resize-none rounded-2xl border border-black/20 bg-black/5 px-4 py-3.5 text-sm text-black outline-none"
            />
          </div>
        )}

        {deliveryFee !== null && !lookingUp && (
          <div>
            <label className="mb-2 block text-sm font-medium text-black">
              Delivery Fee
            </label>
            <div className="rounded-2xl border border-black/20 bg-black/5 px-4 py-3.5 text-sm font-semibold text-black">
              £{Number(deliveryFee).toFixed(2)}
            </div>
          </div>
        )}

        {deliveryFee !== null && !lookingUp && (
          <div className="rounded-2xl border border-red-400/40 bg-red-50 px-4 py-4">
            <p className="text-sm font-semibold text-red-700">
              Important Delivery Notice
            </p>
            <p className="mt-1 text-sm text-red-700/90">
              Please be ready to receive your order when the rider arrives. The
              rider will wait for a maximum of <b>5 minutes</b>. After this
              time, the rider will leave and <b>no refund</b> will be issued.
            </p>
          </div>
        )}

        <div>
          <label className="mb-2 block text-sm font-medium text-black">
            Notes <span className="text-black/50">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => {
              if (isGuest) return;
              setNotes(e.target.value);
            }}
            onFocus={requireLogin}
            onClick={requireLogin}
            readOnly={isGuest}
            rows={5}
            placeholder="Add any extra instructions"
            className="w-full resize-none rounded-2xl border border-black/20 bg-white px-4 py-3.5 text-sm text-black outline-none transition focus:border-[#fa9324] focus:ring-4 focus:ring-[#fa9324]/20"
          />
        </div>

        {showRestrictionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 text-black shadow-xl">
              <h2 className="text-lg font-semibold">
                We do not deliver to hospitals and schools
              </h2>

              <p className="mt-3 text-sm text-black/90">
                Your order cannot be delivered to a <b>hospital or school</b>{" "}
                address. Any delivery order placed to these locations will be{" "}
                <b>cancelled</b>.
              </p>

              <div className="mt-5 flex justify-center">
                <button
                  onClick={() => {
                    setHasAgreedRestriction(true);
                    setShowRestrictionModal(false);
                  }}
                  className="inline-flex items-center justify-center rounded-xl bg-[#fa9324] px-5 py-3 font-semibold"
                >
                  Agree
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
